/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React from 'react';
import { RefreshCw, AlertTriangle } from 'lucide-react';

interface ErrorBoundaryProps {
  children: React.ReactNode;
  /** اسم القسم — بيتسجل في الـ console عشان يسهل معرفة مين اللي وقع بالظبط. */
  sectionName?: string;
  /** رسالة مخصّصة تظهر للمستخدم بدل الرسالة الافتراضية. */
  fallbackMessage?: string;
  // مش prop حقيقي بيوصل للمكوّن (React بتاخده لوحدها قبل ما تعدي الـ props)، لكن
  // لازم نعرّفه هنا عشان مفيش @types/react متثبّتة في المشروع أصلاً تدي TypeScript
  // فكرة إن "key" مسموح دايمًا على أي JSX element.
  key?: React.Key;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

/**
 * Error Boundary عام — بيلف أي قسم/تبويب في التطبيق عشان لو حصل خطأ أثناء الـ render
 * (زي مكوّن بيرجع undefined بدل function، أو خاصية ناقصة) الخطأ يتحجز جوه القسم نفسه
 * فقط بدل ما يوقّع التطبيق بالكامل على شاشة بيضاء. كمان بيطبع تفاصيل واضحة في الـ
 * console (component stack) عشان تسهيل تتبّع سبب المشكلة الحقيقي، خصوصًا في الإصدار
 * المبني (production) اللي بيدي رسائل مبهمة زي "d is not a function".
 */
export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  // تعريف صريح لـ props/state هنا لأن مشروع الفرونت مش عنده @types/react متثبّتة
  // أصلاً (نفس وضع باقي الملفات)، فـ TypeScript مش بيلقط خصائص React.Component
  // تلقائيًا من غير الأنواع الرسمية.
  props: Readonly<ErrorBoundaryProps>;
  state: ErrorBoundaryState;
  forceUpdate!: (callback?: () => void) => void;

  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.props = props;
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // eslint-disable-next-line no-console
    console.error(
      `[ErrorBoundary${this.props.sectionName ? `: ${this.props.sectionName}` : ''}] حصل خطأ أثناء عرض هذا القسم:`,
      error,
      '\nComponent stack:',
      errorInfo.componentStack
    );
  }

  // بديل بسيط لـ this.setState (اللي مش معرّف في الأنواع هنا لغياب @types/react)
  // بيعمل نفس الغرض تمامًا: يحدّث الـ state ويطلب من React يعيد الـ render.
  private handleRetry = () => {
    this.state = { hasError: false, error: null };
    this.forceUpdate();
  };

  render() {
    if (this.state.hasError) {
      return (
        <div
          role="alert"
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '12px',
            padding: '48px 24px',
            textAlign: 'center',
            minHeight: '240px',
          }}
        >
          <AlertTriangle style={{ width: 32, height: 32, opacity: 0.7 }} />
          <p style={{ margin: 0, fontWeight: 600 }}>
            {this.props.fallbackMessage || 'حصلت مشكلة أثناء تحميل هذا القسم.'}
          </p>
          <p style={{ margin: 0, fontSize: '13px', opacity: 0.7 }}>
            جرّب تعيد المحاولة، ولو المشكلة استمرت جرّب تعمل تحديث للصفحة (F5).
          </p>
          <button
            type="button"
            onClick={this.handleRetry}
            className="btn"
            style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}
          >
            <RefreshCw style={{ width: 16, height: 16 }} />
            إعادة المحاولة
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}
